// Edit a user's identity + profile from the family drill-in.
//
// 🔴 THE ONE THING THIS SCREEN MUST NEVER DO QUIETLY: change someone's login
// identity without telling the operator what that costs. The access JWT carries
// `email`/`username` and a generation counter, so the API bumps
// `users.auth_generation` when either moves — instantly killing every access and
// refresh token the user holds, on every device. Editing a display name, phone,
// country or locale does not. The dialog surfaces that difference live, BEFORE
// the operator commits, rather than in a support ticket an hour later.
//
// Only genuinely-changed fields are sent. The API body is `.strict()` and
// compares against the stored row, so an untouched field is neither transmitted
// nor able to trigger a spurious revocation.

import { Input } from '@fajrarabi/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConfirmDialog } from '../ConfirmDialog';
import { Icon } from '../Icon';
import { useToast } from '../Toaster';

import { updateUser, type AdminUserDetail, type UserEditPatch } from '~/lib/admin';

/** The editable slice of a user, as strings the form can hold. */
interface FormState {
  email: string;
  username: string;
  displayName: string;
  country: string;
  phone: string;
  locale: 'ar' | 'en';
  status: 'active' | 'suspended' | 'invited';
}

function toForm(u: AdminUserDetail): FormState {
  return {
    email: u.email ?? '',
    username: u.username ?? '',
    displayName: u.displayName ?? '',
    country: u.country ?? '',
    phone: u.phone ?? '',
    locale: u.locale,
    status: u.status,
  };
}

/** '' in a text field means "cleared" for a nullable column, so it maps to null
 *  rather than to the empty string — which the DB would happily store and which
 *  would then fail the `users_email_or_username` check for the wrong reason. */
function nullable(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

// Mirrors the server-side check constraint `users_username_format` exactly, so
// the operator gets a field error instead of a round trip ending in a 400.
const USERNAME_RE = /^[a-z][a-z0-9_.]{2,39}$/;
// Deliberately permissive — the API runs the authoritative check. This only
// catches the obvious typo before a network call.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function UserEditDialog({
  open,
  onOpenChange,
  user,
  /** Which query keys to refresh once the edit lands. */
  invalidateKeys = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUserDetail | null;
  invalidateKeys?: readonly (readonly unknown[])[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState | null>(null);

  // Re-seed from the server row every time the dialog opens: a half-finished
  // edit from a previous opening must never be presented as the current state.
  useEffect(() => {
    if (open && user) setForm(toForm(user));
  }, [open, user]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  // ── What actually changed, and what it costs ─────────────────────────────
  const patch = useMemo<UserEditPatch>(() => {
    if (!user || !form) return {};
    const out: UserEditPatch = {};
    const email = nullable(form.email);
    const username = nullable(form.username);
    const displayName = nullable(form.displayName);
    const country = nullable(form.country);
    const phone = nullable(form.phone);

    if (email !== (user.email ?? null)) out.email = email;
    if (username !== (user.username ?? null)) out.username = username;
    if (displayName !== (user.displayName ?? null)) out.displayName = displayName;
    if (country !== (user.country ?? null)) out.country = country;
    if (phone !== (user.phone ?? null)) out.phone = phone;
    if (form.locale !== user.locale) out.locale = form.locale;
    if (form.status !== user.status) out.status = form.status;
    return out;
  }, [user, form]);

  const changedCount = Object.keys(patch).length;
  const identityMoves = patch.email !== undefined || patch.username !== undefined;

  // ── Validation ───────────────────────────────────────────────────────────
  const emailError =
    form && nullable(form.email) !== null && !EMAIL_RE.test(form.email.trim())
      ? 'That does not look like an email address.'
      : null;
  const usernameError =
    form &&
    nullable(form.username) !== null &&
    !USERNAME_RE.test(form.username.trim().toLowerCase())
      ? 'Letters, digits, dot or underscore. 3–40 characters, starting with a letter.'
      : null;
  const countryError =
    form && nullable(form.country) !== null && form.country.trim().length !== 2
      ? 'Two-letter country code, e.g. QA.'
      : null;
  // The DB refuses a live row with neither identity, so the dialog refuses it
  // first — with an explanation, rather than a constraint name.
  const identityError =
    form && nullable(form.email) === null && nullable(form.username) === null
      ? 'A user must keep at least one of email or username.'
      : null;

  const invalid = Boolean(emailError ?? usernameError ?? countryError ?? identityError);

  const mutation = useMutation({
    mutationFn: () => updateUser(user!.id, patch),
    onSuccess: async (result) => {
      // The edited row itself, plus whatever list the caller came from.
      await qc.invalidateQueries({ queryKey: ['user', user!.id] });
      await Promise.all(
        invalidateKeys.map((key) => qc.invalidateQueries({ queryKey: key as unknown[] })),
      );
      toast(
        result.sessionsRevoked
          ? `Saved. ${result.changed.length} field${result.changed.length === 1 ? '' : 's'} changed — the user has been signed out everywhere.`
          : `Saved ${result.changed.length} change${result.changed.length === 1 ? '' : 's'}.`,
        { tone: 'success' },
      );
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit user"
      description={
        user
          ? `Identity and profile for ${user.displayName ?? user.email ?? user.username ?? 'this user'}.`
          : undefined
      }
      confirmLabel="Save changes"
      tone="primary"
      // A revocation signs a real customer out of every device they own. That
      // earns the typed-confirmation tier; a phone-number fix does not.
      requireTyped={identityMoves ? 'SIGN OUT' : undefined}
      requireTypedLabel={
        identityMoves ? (
          <>
            Type <strong>SIGN OUT</strong> to confirm you intend to end this user&rsquo;s sessions.
          </>
        ) : undefined
      }
      disabled={!form || changedCount === 0 || invalid || mutation.isPending}
      onConfirm={async () => {
        await mutation.mutateAsync();
      }}
    >
      {form && user ? (
        <div className="space-y-3">
          {/* The consequence banner. Live, above the fields that cause it, so
              it is read before the value is typed rather than after. */}
          {identityMoves && (
            <p className="flex items-start gap-1.5 rounded-lg bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
              <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>
                Changing the email or username <strong>signs this user out everywhere</strong>{' '}
                immediately — every device, and any saved session. They will need their new details
                to sign back in.
                {patch.email !== undefined && ' The new address will show as unverified.'}
              </span>
            </p>
          )}

          <Field
            id="edit-email"
            label="Email"
            hint="Blank for a learner account, which signs in with a username instead."
            error={emailError ?? identityError}
          >
            <Input
              id="edit-email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              autoComplete="off"
              aria-invalid={emailError != null}
            />
          </Field>

          <Field
            id="edit-username"
            label="Username"
            hint="Kids sign in with this. Lowercase letters, digits, dot or underscore."
            error={usernameError}
          >
            <Input
              id="edit-username"
              value={form.username}
              onChange={(e) => set('username', e.target.value)}
              autoComplete="off"
              aria-invalid={usernameError != null}
            />
          </Field>

          <Field id="edit-display-name" label="Display name">
            <Input
              id="edit-display-name"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              autoComplete="off"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field id="edit-country" label="Country" error={countryError}>
              <Input
                id="edit-country"
                value={form.country}
                onChange={(e) => set('country', e.target.value.toUpperCase())}
                placeholder="QA"
                maxLength={2}
                autoComplete="off"
                aria-invalid={countryError != null}
              />
            </Field>
            <Field id="edit-phone" label="Phone">
              <Input
                id="edit-phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                autoComplete="off"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field id="edit-locale" label="Language">
              <select
                id="edit-locale"
                value={form.locale}
                onChange={(e) => set('locale', e.target.value as 'ar' | 'en')}
                className="h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
              >
                <option value="ar">Arabic</option>
                <option value="en">English</option>
              </select>
            </Field>
            <Field
              id="edit-status"
              label="Status"
              hint={
                form.status === 'suspended' && user.status !== 'suspended'
                  ? 'Suspending also signs them out everywhere.'
                  : undefined
              }
            >
              <select
                id="edit-status"
                value={form.status}
                onChange={(e) => set('status', e.target.value as FormState['status'])}
                className="h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="invited">Invited</option>
              </select>
            </Field>
          </div>

          <p className="text-xs text-admin-400">
            {changedCount === 0
              ? 'Nothing changed yet.'
              : `${changedCount} field${changedCount === 1 ? '' : 's'} will be saved. The change is recorded in the audit log.`}
          </p>
        </div>
      ) : (
        <div className="h-64 animate-pulse rounded-xl bg-admin-100" />
      )}
    </ConfirmDialog>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-admin-700">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p role="alert" className="mt-1 text-xs text-error-600">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-admin-400">{hint}</p>
      ) : null}
    </div>
  );
}
